

export const dynamic = 'force-dynamic';

import { useEffect, useState, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { MAIN_URL } from '@/lib/api';

function ImpersonateContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Setting up session...');

  useEffect(() => {
    const token = searchParams.get('token');
    const name = searchParams.get('name');

    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing token');
      return;
    }

    // Store the impersonation token
    localStorage.setItem('token', token);
    document.cookie = `token=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;

    // Fetch user data with the new token
    const initSession = async () => {
      try {
        const response = await fetch(`${MAIN_URL}/api/user`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to validate session');
        }

        const data = await response.json();
        const userData = data.data || data;
        
        // Update auth context with impersonated user
        setUser(userData);
        
        setStatus('success');
        setMessage(`Logged in as ${name || userData.name}. Redirecting...`);
        
        // Redirect to dashboard after a brief delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } catch (error) {
        console.error('Impersonation error:', error);
        setStatus('error');
        setMessage('Failed to establish session. Please try again.');
      }
    };

    initSession();
  }, [searchParams, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-amber-100">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        <div className="mb-6">
          {status === 'loading' && (
            <div className="w-16 h-16 mx-auto border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          )}
          {status === 'success' && (
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {status === 'error' && (
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}
        </div>
        
        <h1 className="text-xl font-bold text-stone-800 mb-2">
          {status === 'loading' && 'Setting up Session...'}
          {status === 'success' && 'Login Successful!'}
          {status === 'error' && 'Login Failed'}
        </h1>
        
        <p className="text-stone-600">{message}</p>
        
        {status === 'error' && (
          <button
            onClick={() => navigate('/login')}
            className="mt-6 px-6 py-2 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors"
          >
            Go to Login
          </button>
        )}
      </div>
    </div>
  );
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-amber-100">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        <div className="w-16 h-16 mx-auto border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    </div>
  );
}

export default function ImpersonatePage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <ImpersonateContent />
    </Suspense>
  );
}
